import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  List,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  ListItem
} from '@mui/material';
import Grid from '@mui/material/Grid';

import {
  CloudUpload as UploadIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Delete as DeleteIcon,
  Tag as TagIcon,
  Visibility as PreviewIcon
} from '@mui/icons-material';
import {
  documentsService,
  UploadDocumentData,
  DocumentType,
  DocumentTag
} from '@services/documentsService';
import { useAuth } from '@contexts/AuthContext';

interface AddDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  petId: string;
  petName: string;
}

interface UploadFile {
  id: string;
  file: File;
  preview?: string;
  uploading: boolean;
  error?: string;
}

const AddDocumentDialog: React.FC<AddDocumentDialogProps> = ({
  open,
  onClose,
  onSuccess,
  petId,
  petName
}) => {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [documentType, setDocumentType] = useState<DocumentType>('medical');
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [description, setDescription] = useState('');
  const [availableTags, setAvailableTags] = useState<DocumentTag[]>([]);
  const [enableOCR, setEnableOCR] = useState(true);

  const documentTypes: { value: DocumentType; label: string }[] = [
    { value: 'medical', label: 'Document Medical' },
    { value: 'vaccination', label: 'Certificat Vaccinare' },
    { value: 'lab_result', label: 'Rezultat Laborator' },
    { value: 'xray', label: 'Radiografie' },
    { value: 'prescription', label: 'Reteta' },
    { value: 'invoice', label: 'Factura' },
    { value: 'consent', label: 'Consimtamant' },
    { value: 'insurance', label: 'Asigurare' },
    { value: 'passport', label: 'Pasaport Animal' },
    { value: 'microchip', label: 'Certificat Microchip' },
    { value: 'other', label: 'Altele' }
  ];

  const commonTags = [
    'Urgent',
    'Rezultat Pozitiv',
    'Rezultat Negativ',
    'Control',
    'Tratament',
    'Chirurgie',
    'Analize',
    'Vaccinare',
    'Deparazitare',
    'Certificat',
    'Raport'
  ];

  const maxFileSize = 20 * 1024 * 1024; // 20MB
  const allowedTypes = [
    'application/pdf',
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/webp',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ];

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles) return;

    const newFiles: UploadFile[] = [];
    
    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      
      // Validate file type
      if (!allowedTypes.includes(file.type)) {
        setError(`Tipul fisierului ${file.name} nu este acceptat. Tipuri permise: PDF, JPG, PNG, DOCX.`);
        continue;
      }

      // Validate file size
      if (file.size > maxFileSize) {
        setError(`Fisierul ${file.name} este prea mare. Dimensiunea maxima: 20MB.`);
        continue;
      }

      const uploadFile: UploadFile = {
        id: Date.now().toString() + i,
        file,
        uploading: false
      };

      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFiles(prev => prev.map(f => 
            f.id === uploadFile.id 
              ? { ...f, preview: e.target?.result as string }
              : f
          ));
        };
        reader.readAsDataURL(file);
      }

      newFiles.push(uploadFile);
    }

    setFiles(prev => [...prev, ...newFiles]);
    setError(null);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const droppedFiles = event.dataTransfer.files;
    
    // Convert FileList to input change event
    const input = fileInputRef.current;
    if (input) {
      const dt = new DataTransfer();
      for (let i = 0; i < droppedFiles.length; i++) {
        dt.items.add(droppedFiles[i]);
      }
      input.files = dt.files;
      handleFileSelect({ target: input } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleRemoveFile = (fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags(prev => [...prev, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(prev => prev.filter(tag => tag !== tagToRemove));
  };

  const getFileIcon = (fileType: string) => {
    if (fileType === 'application/pdf') return <PdfIcon color="error" />;
    if (fileType.startsWith('image/')) return <ImageIcon color="primary" />;
    return <FileIcon />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleUpload = async () => {
    if (!user?.clinicId || files.length === 0) {
      setError('Selectati cel putin un fisier pentru upload');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const uploadPromises = files.map(async (uploadFile) => {
        try {
          setFiles(prev => prev.map(f => 
            f.id === uploadFile.id ? { ...f, uploading: true, error: undefined } : f
          ));

          const uploadData: UploadDocumentData = {
            file: uploadFile.file,
            petId,
            clinicId: user.clinicId!,
            type: documentType,
            description,
            tags,
            enableOCR
          };

          const result = await documentsService.uploadDocument(uploadData);
          
          setFiles(prev => prev.map(f => 
            f.id === uploadFile.id ? { ...f, uploading: false } : f
          ));

          return result;
        } catch (error) {
          console.error(`Error uploading ${uploadFile.file.name}:`, error);
          setFiles(prev => prev.map(f => 
            f.id === uploadFile.id 
              ? { ...f, uploading: false, error: 'Eroare la upload' }
              : f
          ));
          throw error;
        }
      });

      await Promise.all(uploadPromises);
      
      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Error uploading documents:', error);
      setError('Eroare la incarcarea documentelor');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFiles([]);
    setDocumentType('medical');
    setTags([]);
    setNewTag('');
    setDescription('');
    setError(null);
    setEnableOCR(true);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <UploadIcon />
          <Typography variant="h6">
            Adauga Document pentru {petName}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Document Type and Description */}
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Tip Document</InputLabel>
              <Select
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value as DocumentType)}
                label="Tip Document"
              >
                {documentTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Descriere"
              fullWidth
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descriere scurta a documentului"
            />
          </Grid>

          {/* Tags */}
          <Grid size={12}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Tag-uri
              </Typography>
              
              <Box display="flex" gap={1} alignItems="center" mb={1}>
                <TextField
                  size="small"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Adauga tag"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                />
                <Button size="small" onClick={handleAddTag} disabled={!newTag.trim()}>
                  Adauga
                </Button>
              </Box>

              <Box display="flex" gap={0.5} flexWrap="wrap" mb={1}>
                {commonTags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    variant={tags.includes(tag) ? "filled" : "outlined"}
                    onClick={() => {
                      if (tags.includes(tag)) {
                        handleRemoveTag(tag);
                      } else {
                        setTags(prev => [...prev, tag]);
                      }
                    }}
                  />
                ))}
              </Box>

              {tags.length > 0 && (
                <Box display="flex" gap={0.5} flexWrap="wrap">
                  {tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      onDelete={() => handleRemoveTag(tag)}
                      color="primary"
                      size="small"
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Grid>

          {/* File Upload Area */}
          <Grid size={12}>
            <Card variant="outlined">
              <CardContent>
                <Box
                  sx={{
                    border: '2px dashed #ccc',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'action.hover'
                    }
                  }}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="h6" gutterBottom>
                    Trageti fisierele aici sau faceti click pentru selectare
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tipuri acceptate: PDF, JPG, PNG, DOCX • Dimensiune maxima: 20MB
                  </Typography>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    hidden
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                    onChange={handleFileSelect}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Files List */}
          {files.length > 0 && (
            <Grid size={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Fisiere selectate ({files.length})
                  </Typography>
                  
                  <List>
                    {files.map((uploadFile) => (
                      <ListItem key={uploadFile.id}>
                        <ListItemIcon>
                          {getFileIcon(uploadFile.file.type)}
                        </ListItemIcon>
                        <ListItemText
                          primary={uploadFile.file.name}
                          secondary={
                            <Box>
                              <Typography variant="body2">
                                {formatFileSize(uploadFile.file.size)}
                              </Typography>
                              {uploadFile.uploading && (
                                <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                                  <CircularProgress size={16} />
                                  <Typography variant="caption">Se incarca...</Typography>
                                </Box>
                              )}
                              {uploadFile.error && (
                                <Typography variant="caption" color="error">
                                  {uploadFile.error}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        
                        {uploadFile.preview && (
                          <Box mr={1}>
                            <img
                              src={uploadFile.preview}
                              alt="Preview"
                              style={{
                                width: 40,
                                height: 40,
                                objectFit: 'cover',
                                borderRadius: 4
                              }}
                            />
                          </Box>
                        )}
                        
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => handleRemoveFile(uploadFile.id)}
                            disabled={uploadFile.uploading}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Anuleaza
        </Button>
        
        <Button
          variant="contained"
          startIcon={<UploadIcon />}
          onClick={handleUpload}
          disabled={loading || files.length === 0}
        >
          {loading ? <CircularProgress size={20} /> : `Incarca ${files.length} fisier${files.length !== 1 ? 'e' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddDocumentDialog;
